import { AppModule } from 'app/module';
import { user } from 'app/user';
import { app } from 'app/app';
import { Input } from 'module/input/main';
import { Toggle } from 'module/toggle/main';
import { deleteJson, getJson, postJson } from 'util/xhr';
import { serializeJson } from 'util/form';
import { trackGa4 } from 'util/googleAnalytics';
import { GdprModal } from 'module/gdprModal/main';
import { Spot } from 'module/spot/main';
import {
    API_ENDPOINT_JOBALARM_CREATE,
    API_ENDPOINT_JOBALARM_DELETE,
    API_ENDPOINT_JOBALARM_NOTIFICATION_INTERVAL,
} from 'app/endpoints';
import { MAIL_NEVER } from 'app/mailSubscriptionInterval';
import * as jobAlarmResultState from 'util/jobAlarmResultStates';
import { ResultState } from 'module/resultState/main';
import { JobAlarmNoSpamText } from 'module/jobAlarmNoSpamText/main';
import { generic } from 'util/validationMessages';
import { RadioGroup } from 'module/radioGroup/main';
import Template from './template.hbs';
import './styles.scss';

export class AlarmDisruptorPill extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    loadUserMailInterval() {
        this.mailIntervalRequest?.abort();
        this.mailIntervalRequest = getJson(
            API_ENDPOINT_JOBALARM_NOTIFICATION_INTERVAL,
            {},
            (response) => this.onMailIntervalResponse(response),
            () => this.onMailIntervalResponse({ jobAlarmMailSubscription: MAIL_NEVER }),
        );
    }

    onMailIntervalResponse({ jobAlarmMailSubscription }) {
        if (!jobAlarmMailSubscription) return;

        // Update props of radio group so the input with the subscription value is checked
        this.intervalChoice?.setChecked(jobAlarmMailSubscription);
    }

    ready() {
        // Default selection
        if (user.isLoggedIn()) {
            app.events.on('firstUserInteraction', () => this.loadUserMailInterval());
        }
    }

    domBindings() {
        return {
            anchor: '.m-alarmDisruptorPill__anchor',
            box: '.m-alarmDisruptorPill__box',
            button: '.m-alarmDisruptorPill__pill',
            buttonExisting: '.m-alarmDisruptorPill__pill--active',
            buttonLoggedOut: '.m-alarmDisruptorPill__pill--loggedOut',
            buttonNoExisting: '.m-alarmDisruptorPill__pill--noExisting',
            closeIcon: '.m-alarmDisruptorPill__closeSvg',
            container: '.m-alarmDisruptorPill__container',
            email: '.m-alarmDisruptorPill__inputGroup .m-input',
            form: '.m-alarmDisruptorPill__form',
            gdprModal: '.m-alarmDisruptorPill__gdprModal .m-gdprModal',
            inner: '.m-alarmDisruptorPill__inner',
            inputKeyword: '.m-alarmDisruptorPill__input--keywords',
            inputLocations: '.m-alarmDisruptorPill__input--locations',
            inputRadius: '.m-alarmDisruptorPill__input--radius',
            inputHomeoffice: '.m-alarmDisruptorPill__input--homeoffice',
            inputJobFields: '.m-alarmDisruptorPill__input--jobFields',
            inputEmploymentTypes: '.m-alarmDisruptorPill__input--employmentTypes',
            inputJobLevels: '.m-alarmDisruptorPill__input--jobLevels',
            intervalChoice: '.m-alarmDisruptorPill__intervalChoice',
            noSpamText: '.m-jobAlarmNoSpamText',
            resultState: '.m-alarmDisruptorPill__resultState',
            submitButton: '.m-alarmDisruptorPill__submitButton',
            toggle: '.m-alarmDisruptorPill__toggle .k-c-toggle',
        };
    }

    getPropsFromDom() {
        return {
            active: !!this.dom.inner,
            isLoggedIn: (this.dom.el.getAttribute('data-is-loggedin') === 'true'),
            similarJobsAlarm: this.dom.inner ? this.dom.inner.getAttribute('data-is-similar') : false,
            isInline: this.dom.inner ? (this.dom.inner.getAttribute('data-is-inline') === 'true') : false,
            displayNone: this.dom.box
                ? this.dom.box.classList.contains('m-alarmDisruptorPill__box--displayNone')
                : false,
            email: this.dom.form ? this.dom.form.email.value : false,
            formEvent: this.dom.form ? this.dom.form.getAttribute('data-form-event-action') : '',
            isJobDetail: (this.dom.el.getAttribute('data-is-job-detail') === 'true'),
            notificationSwitch: this.intervalChoice?.getPropsFromDom(),
            query: {
                keywords: this.dom.inputKeyword ? this.dom.inputKeyword.value : '',
                locations: this.dom.inputLocations ? this.dom.inputLocations.value : '',
                radius: this.dom.inputRadius ? this.dom.inputRadius.value : '',
                homeoffice: this.dom.inputHomeoffice ? this.dom.inputHomeoffice.value : false,
                jobFields: this.dom.inputJobFields ? this.dom.inputJobFields.value : '',
                employmentTypes: this.dom.inputEmploymentTypes ? this.dom.inputEmploymentTypes.value : '',
                jobLevels: this.dom.inputJobLevels ? this.dom.inputJobLevels.value : '',
            },
            keywords: this.dom.box ? this.dom.box.getAttribute('data-keywords') : false,
            location: this.dom.box ? this.dom.box.getAttribute('data-location') : false,
            jobAlarmId: this.dom.button ? this.dom.button.getAttribute('data-jobalarm-id') : false,
            exists: this.dom.button ? this.dom.button.getAttribute('data-jobalarm-id') : false,
        };
    }

    domEvents() {
        this.dom.buttonExisting?.addEventListener?.('click', () => {
            if (!user.isLoggedIn()) return;
            this.events.emit('openEditModal', this.props.jobAlarmId);
        });

        this.dom.buttonNoExisting?.addEventListener?.('click', () => this.openAlarmBox());
        this.dom.closeIcon?.addEventListener?.('click', () => this.closeAlarmBox());
        this.dom.form?.addEventListener?.('submit', (e) => this.submit(e));
    }

    closeAlarmBox() {
        this.updateProps({ displayNone: true, success: false, errors: false }, true);
    }

    openAlarmBox(timeout = false) {
        if (localStorage?.getItem?.('wasOpen') !== 'true') {
            this.updateProps({ displayNone: false }, true);
        } else if (!timeout) {
            this.updateProps({ displayNone: false }, true);
        }

        localStorage?.setItem?.('wasOpen', true);
    }

    validationRules() {
        this.validation.useMessages(generic);
        this.validation.addRule(this.email, {
            sequential: true,
            rules: ['required', 'email'],
        });
    }

    subs() {
        if (this.dom.gdprModal) {
            this.gdprModal = new GdprModal(this.dom.gdprModal);
        }

        if (this.dom.toggle) {
            this.toggle = new Toggle(this.dom.toggle);
        }

        if (this.dom.email) {
            this.email = new Input(this.dom.email);
        }

        if (this.dom.spot) {
            this.spot = new Spot(this.dom.spot);
        }

        if (this.dom.resultState) {
            this.resultState = new ResultState(this.dom.resultState);
        }

        if (this.dom.noSpamText) {
            this.noSpamText = new JobAlarmNoSpamText(this.dom.noSpamText);
        }

        if (this.dom.intervalChoice) {
            this.intervalChoice = new RadioGroup(this.dom.intervalChoice);
        }
    }

    subEvents() {
        if (this.gdprModal) {
            this.gdprModal.events.on('acceptGdpr', () => this.acceptGdpr());
            this.gdprModal.events.on('removeModalStyle', () => this.closeGdprModal());
        }

        if (this.email) {
            this.email.events.on('blur', (e) => {
                this.updateProps({ emailInputValue: e.arguments[0].target.value });
            });
        }

        if (this.intervalChoice) {
            this.intervalChoice.events.on('change', () => {
                this.updateProps({
                    notificationSwitch: this.intervalChoice.getPropsFromDom(),
                });
            });
        }
    }

    submit(e) {
        e.preventDefault();
        if (this.validation.validate()) {
            const json = serializeJson(this.dom.form);
            if (this.dom.intervalChoice) {
                json.mailNotificationInterval = parseInt(this.intervalChoice.getValue(), 10);
            }
            this.createAlarm(json);
        }
    }

    createAlarm(data) {
        if (this.request) {
            this.request.abort();
        }

        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = true;
        }

        this.request = postJson(
            API_ENDPOINT_JOBALARM_CREATE,
            { data },
            (response) => this.onResponse(response),
            (error) => this.onResponseError(error),
        );
    }

    removeAlarm(data) {
        if (this.request) {
            this.request.abort();
        }

        const url = `${API_ENDPOINT_JOBALARM_DELETE}/${this.props.jobAlarmId}`;
        this.request = deleteJson(
            url,
            { data },
            (response) => this.onRemoveResponse(response),
        );
    }

    onRemoveResponse(response, calledFromController = false) {
        this.updateProps(Object.assign(response, { success: false }), true);
        if (!calledFromController && user.isLoggedIn()) {
            this.events.emit('jobAlarmRemoveResponse', response);
            this.events.emit('showSnackbar');
        }
    }

    onResponse(response, calledFromController = false) {
        if (response.gdpr) {
            this.gdprModal.setProps(response.gdpr);
            this.gdprModal.updateProps({ visible: true, scrollTop: 0, active: true }, true);
            this.gdprModal.checkIfScrollEnd();
            this.events.emit('addModalStyle');
        } else {
            let resultState;
            if (response.userExists) {
                if (user.isLoggedIn()) {
                    this.updateProps({
                        displayNone: true,
                    }, true);
                } else {
                    resultState = jobAlarmResultState
                        .success(response.email, response.jobAlarmId, response.jobAlarmMailDaily);
                }
            } else {
                resultState = jobAlarmResultState.checkMail();
            }

            this.updateProps({
                ...response,
                resultState,
            }, true);
            if (!calledFromController) {
                const event = {
                    event: 'jobalarm_create_success',
                    user_data: {
                        email: response.email,
                    },
                };

                if (this.props.isJobDetail) {
                    trackGa4({
                        ...event,
                        method: 'Job Alarm Detail',
                    });
                } else if (this.props.isInline) {
                    trackGa4({
                        ...event,
                        method: 'Job Alarm Inline',
                    });
                } else {
                    trackGa4({
                        ...event,
                        method: 'Job Alarm Fixed',
                    });
                }
                if (user.isLoggedIn()) {
                    this.events.emit('jobAlarmResponseSuccess', response);

                    this.events.emit(
                        'showSnackbar',
                        'Job-Alarm wurde angelegt.',
                    );
                }
            }
        }
    }

    onResponseError(response) {
        const errorsMeta = this.validation.handleBackendValidationErrors(response);

        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = false;
        }

        const error = errorsMeta?.general;
        let resultState;
        if (error?.[0].jobalarm_exists) {
            resultState = jobAlarmResultState.alreadyExists();
        } else if (error?.[0].jobalarm_limit_exceeded) {
            resultState = jobAlarmResultState.limitExceeded();
        } else if (error?.[0].jobalarm_exists_for_unconfirmed_email) {
            resultState = jobAlarmResultState.checkMail();
        } else {
            resultState = jobAlarmResultState.generalError();
        }
        this.updateProps({
            resultState,
            displayNone: false,
        }, true);

        if (this.email) {
            this.email.updateProps({ value: this.props.emailInputValue }, true);
        }

        return true;
    }

    acceptGdpr() {
        if (this.gdprModal.validate()) {
            const gdprOptionalCheckBoxSize = Object.keys(this.gdprModal.props.optionalBox.items).length;
            const gdprRequiredCheckBoxSize = Object.keys(this.gdprModal.props.requiredBox.items).length;
            const data = serializeJson(this.dom.form);

            for (let i = 0; i < gdprOptionalCheckBoxSize; i += 1) {
                data[this.gdprModal.props.optionalBox.items[i].name] = this.gdprModal.props.optionalBox.items[i].checked;
            }

            for (let i = 0; i < gdprRequiredCheckBoxSize; i += 1) {
                data[this.gdprModal.props.requiredBox.items[i].name] = this.gdprModal.props.requiredBox.items[i].checked;
            }

            this.gdprModal.closeModal();
            this.createAlarm(data);
        }
    }

    closeGdprModal() {
        this.events.emit('removeModalStyle');
        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = false;
        }
    }
}
